import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { toast } from "sonner";
import { UploadSimple, Trash, CheckCircle, XCircle, PencilSimple, CircleNotch, FileText, LinkSimple } from "@phosphor-icons/react";
import { ScoreInfo } from "@/components/mai/Explain";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORY_META = {
  evidence_asset: { label: "Evidence Asset", color: "#1D4ED8", dest: "Evidence Inventory" },
  country_insight: { label: "Country Insight", color: "#047857", dest: "Country Intelligence" },
  competitor_outcome: { label: "Competitor Outcome", color: "#B45309", dest: "Competitive Landscape" },
};
const DOC_STATUS = {
  processing: { label: "AI Extracting…", color: "#B45309" },
  extracted: { label: "Extracted", color: "#047857" },
  failed: { label: "Failed", color: "#B91C1C" },
  ready: { label: "Ready", color: "#1D4ED8" },
};

const AddLinksDialog = ({ open, onClose, productId, onDone }) => {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const urls = raw.split(/\s+/).filter((u) => u.trim());
  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/ingestion/url`, { product_id: productId, urls });
      const { started, failed, results } = r.data;
      if (started) toast.success(`${started} link(s) fetched — AI extraction started`);
      results.filter((x) => x.status === "failed").forEach((x) => toast.error(`${x.url.slice(0, 60)}: ${x.error}`));
      if (!failed) { setRaw(""); onClose(); }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not ingest links");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-base">Add reference links</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Paste one link per line — web pages and online PDFs (HTA reports, guidance pages, publications). Each link is fetched, read and sent through the same AI fact extraction and review queue as an uploaded document.</p>
        <textarea data-testid="links-textarea" value={raw} onChange={(e) => setRaw(e.target.value)} rows={6}
          placeholder={"https://www.nice.org.uk/guidance/htg405\nhttps://pubmed.ncbi.nlm.nih.gov/..."}
          className="w-full border border-slate-300 p-3 text-xs font-mono focus:outline-none focus:border-slate-500" />
        <button data-testid="links-submit-btn" disabled={busy || !urls.length} onClick={submit}
          className="bg-slate-900 text-white text-xs font-semibold px-4 py-2.5 hover:bg-slate-800 flex items-center justify-center gap-2 disabled:opacity-50 w-full">
          {busy ? <CircleNotch size={14} className="animate-spin" /> : <LinkSimple size={14} weight="bold" />}
          {busy ? "Fetching links…" : `Fetch & extract ${urls.length || ""} link${urls.length === 1 ? "" : "s"}`}
        </button>
      </DialogContent>
    </Dialog>
  );
};

const payloadSummary = (fact) => {
  const p = fact.payload;
  if (fact.category === "evidence_asset")
    return { title: p.name, meta: `${p.type} · ${p.maturity} · ${p.scope}`, detail: p.description };
  if (fact.category === "country_insight")
    return { title: `${p.country_name || "Unmapped market"}${p.requirement_type ? ` — ${p.requirement_type}${p.level ? ` (${p.level})` : ""}` : ""}`, meta: p.insight_type, detail: p.detail };
  return { title: `${p.competitor} (${p.company})${p.country_name ? ` — ${p.country_name}` : ""}`, meta: `${p.outcome}${p.pricing_anchor ? " · pricing anchor" : ""}`, detail: p.detail };
};

function FactEditDialog({ fact, onClose, onSaved }) {
  const [draft, setDraft] = useState(() =>
    Object.fromEntries(Object.entries(fact.payload).map(([k, v]) => [k, Array.isArray(v) ? v.join(", ") : v])));
  const save = async () => {
    const payload = Object.fromEntries(Object.entries(draft).map(([k, v]) => {
      if (Array.isArray(fact.payload[k])) return [k, String(v).split(",").map((s) => s.trim()).filter(Boolean)];
      if (typeof fact.payload[k] === "boolean") return [k, v === true || v === "true"];
      return [k, v];
    }));
    try {
      const r = await axios.patch(`${API}/ingestion/facts/${fact.id}`, { payload });
      toast.success("Fact updated");
      onSaved(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="fact-edit-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-sm">Edit fact before approval</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {Object.entries(draft).map(([k, v]) => (
            <div key={k}>
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">{k.replace(/_/g, " ")}</p>
              {String(v).length > 60 ? (
                <textarea value={v ?? ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                  className="w-full text-xs border border-slate-300 px-2.5 py-2 min-h-[70px]" />
              ) : (
                <input value={v ?? ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                  className="w-full text-xs border border-slate-300 px-2.5 py-2" />
              )}
            </div>
          ))}
        </div>
        <button data-testid="fact-edit-save" onClick={save}
          className="w-full bg-slate-900 text-white text-xs font-semibold py-2.5 hover:bg-slate-800">Save Changes</button>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ fact, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("A decision reason is required to reject a fact."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/ingestion/facts/${fact.id}/reject`, { reason: reason.trim() });
      toast.success("Fact rejected — reason retained in the audit trail");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="fact-reject-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-sm">Reject fact — decision reason required</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Rejected facts are retained with their reason for the approval audit.</p>
        <textarea data-testid="fact-reject-reason" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Why is this fact being rejected?"
          className="w-full text-xs border border-slate-300 px-2.5 py-2 min-h-[80px]" />
        <button data-testid="fact-reject-confirm" disabled={busy} onClick={submit}
          className="w-full bg-red-700 text-white text-xs font-semibold py-2.5 hover:bg-red-800 disabled:opacity-50">
          {busy ? "Rejecting…" : "Reject Fact"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

function AuditTrail({ f }) {
  if (f.status === "pending") return null;
  const unknown = f.approval_status === "approval_status_unknown";
  return (
    <div data-testid={`fact-audit-${f.id}`} className="mt-2 pt-2 border-t border-slate-100">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono-data text-[10px] text-slate-400">
        {unknown && <span data-testid={`fact-unknown-${f.id}`} className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-red-700 text-white">Approval status unknown</span>}
        <span>Reviewer: <span className={f.reviewer ? "text-slate-600" : "text-red-600"}>{f.reviewer || "not recorded"}</span></span>
        <span>Review date: <span className={f.reviewed_at ? "text-slate-600" : "text-red-600"}>{f.reviewed_at ? new Date(f.reviewed_at).toLocaleString() : "not recorded"}</span></span>
        <span>Extracted: {f.created_at ? new Date(f.created_at).toLocaleDateString() : "—"} · confidence {Math.round((f.confidence || 0) * 100)}%</span>
        <span>Page: {f.page_number ?? "not captured"}</span>
        {f.source_document && <span>Source: {f.source_document}</span>}
      </div>
      {f.decision_reason && <p className="text-[10px] text-slate-500 mt-1">Reason: {f.decision_reason}</p>}
      {f.edited_value && <p className="text-[10px] text-amber-700 mt-1">Edited before review — original value retained in the audit record.</p>}
      {unknown && <p className="text-[10px] text-red-600 mt-1">Incomplete review history — this fact is excluded from verified readiness calculations.</p>}
    </div>
  );
}

function FactAuditReport({ productId }) {
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    setReport(null);
    axios.get(`${API}/ingestion/facts-audit/${productId}/latest`)
      .then((r) => { setReport(r.data); setError(false); })
      .catch(() => setError(true));
  }, [productId]);

  const run = async () => {
    setRunning(true);
    try {
      const r = await axios.get(`${API}/ingestion/facts-audit/${productId}/exceptions`);
      setReport(r.data);
      toast.success("Exception report generated");
    } catch {
      toast.error("Could not generate the exception report.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <section data-testid="fact-audit-report" className="bg-white border border-slate-200">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <div>
          <p className="font-heading font-bold text-sm tracking-tight">Approval Audit — Exception Report</p>
          <p className="text-xs text-slate-500 mt-1">Every approved fact must carry a reviewer, source passage and review date. Records missing any of these are marked "Approval status unknown" and excluded from verified readiness.</p>
        </div>
        <button data-testid="run-fact-audit" disabled={running} onClick={run}
          className="bg-slate-900 text-white text-xs font-semibold px-4 py-2 hover:bg-slate-800 disabled:opacity-50 shrink-0">
          {running ? "Auditing…" : "Run exception report"}
        </button>
      </div>
      {error ? (
        <p data-testid="fact-audit-error" className="px-5 py-6 text-center text-xs text-red-600">Could not load the audit report.</p>
      ) : !report ? (
        <p data-testid="fact-audit-loading" className="px-5 py-6 text-center text-xs text-slate-400">Loading audit report…</p>
      ) : (
        <>
          <div className="grid grid-cols-3 divide-x divide-slate-200 border-b border-slate-200">
            {[["Approved facts", report.totals.approved, ""],
              ["Fully documented", report.totals.fully_documented, "text-emerald-700"],
              ["Approval status unknown", report.totals.approval_status_unknown, report.totals.approval_status_unknown > 0 ? "text-red-700" : "text-emerald-700"]].map(([label, value, cls]) => (
              <div key={label} className="px-5 py-4">
                <p className="overline-label text-slate-400">{label}</p>
                <p className={`font-heading font-extrabold text-2xl mt-1 ${cls}`}>{value}</p>
              </div>
            ))}
          </div>
          {report.generated_at === null ? (
            <p data-testid="fact-audit-empty" className="px-5 py-6 text-center text-xs text-slate-400">No exception report generated yet — run one to audit the approved facts.</p>
          ) : report.exceptions.length === 0 ? (
            <p data-testid="fact-audit-clean" className="px-5 py-6 text-center text-xs text-emerald-700">Every approved fact has a complete review history.</p>
          ) : report.exceptions.map((x) => (
            <div key={x.fact_id} data-testid={`fact-exception-${x.fact_id}`} className="px-5 py-3 border-b border-slate-100 last:border-0 flex items-start gap-4">
              <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-red-700 text-white shrink-0 mt-0.5">Unknown</span>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-slate-800 truncate">{(x.original_value && x.original_value.name) || x.supporting_passage || x.fact_id}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Missing: <span className="text-red-600 font-semibold">{x.missing_fields.join(", ")}</span>
                  {" · "}extracted {x.extraction_date ? new Date(x.extraction_date).toLocaleDateString() : "—"} · confidence {Math.round((x.extraction_confidence || 0) * 100)}%
                  {x.source_document && ` · ${x.source_document}`}
                </p>
              </div>
            </div>
          ))}
          {report.generated_at && <p className="px-5 py-2 font-mono-data text-[10px] text-slate-400">Generated {new Date(report.generated_at).toLocaleString()}</p>}
        </>
      )}
    </section>
  );
}

export default function KnowledgePage() {
  const { productId } = useProduct();
  const [docs, setDocs] = useState([]);
  const [facts, setFacts] = useState([]);
  const [tab, setTab] = useState("pending");
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);
  const [linksOpen, setLinksOpen] = useState(false);
  const fileRef = useRef(null);

  const loadDocs = useCallback(() => axios.get(`${API}/ingestion/documents`, { params: { product_id: productId, doc_type: "general" } }).then((r) => setDocs(r.data)), [productId]);
  const loadFacts = useCallback(() => axios.get(`${API}/ingestion/facts`, { params: { product_id: productId } }).then((r) => setFacts(r.data)), [productId]);

  useEffect(() => { loadDocs(); loadFacts(); }, [loadDocs, loadFacts]);

  const processing = docs.some((d) => d.status === "processing");
  useEffect(() => {
    if (!processing) return;
    const t = setInterval(() => { loadDocs(); loadFacts(); }, 4000);
    return () => clearInterval(t);
  }, [processing, loadDocs, loadFacts]);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        form.append("product_id", productId);
        form.append("doc_type", "general");
        try {
          await axios.post(`${API}/ingestion/upload`, form);
          ok += 1;
        } catch (e) {
          toast.error(`${file.name}: ${e.response?.data?.detail || "upload failed"}`);
        }
      }
      if (ok) toast.success(ok === 1 ? "Document uploaded — AI extraction started" : `${ok} documents uploaded — AI extraction started`);
      loadDocs();
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const approve = async (fact) => {
    try {
      const r = await axios.post(`${API}/ingestion/facts/${fact.id}/approve`, {});
      toast.success(r.data.applied || "Approved");
      loadFacts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const approveAll = async () => {
    setApprovingAll(true);
    try {
      const r = await axios.post(`${API}/ingestion/facts/approve-all`, null, { params: { product_id: productId } });
      const { approved, failed } = r.data;
      toast.success(`${approved} facts approved and written to the knowledge graph${failed.length ? ` · ${failed.length} failed` : ""}`);
      loadFacts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk approval failed");
    } finally {
      setApprovingAll(false);
      setConfirmAll(false);
    }
  };

  const deleteDoc = async (id) => {
    await axios.delete(`${API}/ingestion/documents/${id}`);
    toast.success("Document removed");
    loadDocs(); loadFacts();
  };

  const shown = facts.filter((f) => f.status === tab);
  const counts = { pending: 0, approved: 0, rejected: 0 };
  facts.forEach((f) => { counts[f.status] = (counts[f.status] || 0) + 1; });

  return (
    <PageShell pageId="knowledge" overline={`MAI Knowledge Hub™ · Knowledge Acquisition`} title="Upload Documents"
      actions={
        <>
          <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.txt,.md" className="hidden"
            onChange={(e) => e.target.files?.length && upload(e.target.files)} />
          <button data-testid="add-links-btn" onClick={() => setLinksOpen(true)}
            className="border border-slate-300 text-slate-700 text-xs font-semibold px-4 py-2.5 hover:bg-slate-50 flex items-center gap-2">
            <LinkSimple size={14} weight="bold" /> Add Links
          </button>
          <Link to="/console/bulk-upload" data-testid="bulk-catalogue-link"
            className="text-[11px] text-slate-400 hover:text-slate-600 underline self-center mr-1">
            Bulk catalogue (advanced)
          </Link>
          <button data-testid="upload-document-btn" disabled={uploading} onClick={() => fileRef.current?.click()}
            className="bg-slate-900 text-white text-xs font-semibold px-4 py-2.5 hover:bg-slate-800 flex items-center gap-2 disabled:opacity-50">
            {uploading ? <CircleNotch size={14} className="animate-spin" /> : <UploadSimple size={14} weight="bold" />}
            Upload Documents
          </button>
        </>
      }>
      <section data-testid="documents-section" className="bg-white border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200">
          <p className="font-heading font-bold text-sm tracking-tight">Ingested Documents</p>
          <p className="text-xs text-slate-500 mt-1">HTA reports, publications and payer documents. GPT-5.5 extracts structured facts for your review — nothing enters the knowledge graph without human approval.</p>
        </div>
        {docs.length === 0 ? (
          <p className="px-5 py-10 text-center text-xs text-slate-400">No documents yet — upload a PDF, Word or text file to begin.</p>
        ) : docs.map((d) => {
          const s = DOC_STATUS[d.status] || DOC_STATUS.ready;
          return (
            <div key={d.id} data-testid={`document-row-${d.id}`} className="px-5 py-3 border-b border-slate-100 last:border-0 flex items-center gap-4">
              {d.source_url
                ? <LinkSimple size={18} className="text-slate-300 shrink-0" />
                : <FileText size={18} className="text-slate-300 shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-slate-800 truncate">
                  {d.filename}
                  {d.source_url && (
                    <a data-testid={`doc-source-link-${d.id}`} href={d.source_url} target="_blank" rel="noreferrer"
                      className="ml-2 text-[10px] text-blue-700 hover:underline font-normal">{d.source_url.slice(0, 60)}…</a>
                  )}
                </p>
                <p className="font-mono-data text-[10px] text-slate-400 mt-0.5">
                  {new Date(d.created_at).toLocaleString()} · {Math.round(d.chars / 1000)}k chars
                  {d.total_facts > 0 && ` · ${d.total_facts} facts extracted`}
                  {d.error && ` · ${d.error}`}
                </p>
              </div>
              <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 text-white flex items-center gap-1.5" style={{ background: s.color }}>
                {d.status === "processing" && <CircleNotch size={10} className="animate-spin" />}{s.label}
              </span>
              <button data-testid={`delete-doc-${d.id}`} onClick={() => deleteDoc(d.id)} className="text-slate-300 hover:text-red-600 p-1"><Trash size={15} /></button>
            </div>
          );
        })}
      </section>

      <section data-testid="review-queue" className="bg-white border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <p className="font-heading font-bold text-sm tracking-tight">Fact Review Queue <ScoreInfo scoreId="extraction_confidence" /></p>
            <p className="text-xs text-slate-500 mt-1">Approve to write into the knowledge graph · reject to discard · edit before approval.</p>
          </div>
          <div className="flex items-center gap-1">
            {counts.pending > 0 && (confirmAll ? (
              <div className="flex items-center gap-1 mr-2">
                <button data-testid="approve-all-confirm" disabled={approvingAll} onClick={approveAll}
                  className="text-[11px] font-semibold px-3 py-1.5 bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50 flex items-center gap-1.5">
                  {approvingAll && <CircleNotch size={11} className="animate-spin" />}
                  Confirm — approve {counts.pending} facts
                </button>
                <button data-testid="approve-all-cancel" disabled={approvingAll} onClick={() => setConfirmAll(false)}
                  className="text-[11px] font-semibold px-3 py-1.5 text-slate-500 border border-slate-200 hover:bg-slate-50">Cancel</button>
              </div>
            ) : (
              <button data-testid="approve-all-btn" onClick={() => setConfirmAll(true)}
                className="text-[11px] font-semibold px-3 py-1.5 mr-2 bg-emerald-700 text-white hover:bg-emerald-800 flex items-center gap-1.5">
                <CheckCircle size={12} weight="bold" /> Approve all ({counts.pending})
              </button>
            ))}
            {["pending", "approved", "rejected"].map((t) => (
              <button key={t} data-testid={`facts-tab-${t}`} onClick={() => setTab(t)}
                className={`text-[11px] font-semibold px-3 py-1.5 capitalize ${tab === t ? "bg-slate-900 text-white" : "text-slate-500 border border-slate-200 hover:bg-slate-50"}`}>
                {t} ({counts[t] || 0})
              </button>
            ))}
          </div>
        </div>
        {shown.length === 0 ? (
          <p className="px-5 py-10 text-center text-xs text-slate-400">No {tab} facts.</p>
        ) : shown.map((f) => {
          const meta = CATEGORY_META[f.category];
          const s = payloadSummary(f);
          return (
            <div key={f.id} data-testid={`fact-card-${f.id}`} className="px-5 py-4 border-b border-slate-100 last:border-0">
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 text-white" style={{ background: meta.color }}>{meta.label}</span>
                    <span className="font-mono-data text-[10px] text-slate-400">confidence {Math.round(f.confidence * 100)}% · → {meta.dest}</span>
                  </div>
                  <p className="text-xs font-semibold text-slate-800 mt-1.5">{s.title}<span className="ml-2 font-normal text-slate-400">{s.meta}</span></p>
                  {s.detail && <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{s.detail}</p>}
                  {f.source_quote && <p className="text-[10px] text-slate-400 italic mt-1.5 border-l-2 border-slate-200 pl-2">“{f.source_quote}”</p>}
                  {f.applied && <p className="text-[10px] text-emerald-700 mt-1.5 font-semibold">✓ {f.applied}</p>}
                  <AuditTrail f={f} />
                </div>
                {f.status === "pending" && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button data-testid={`fact-edit-${f.id}`} onClick={() => setEditing(f)} title="Edit"
                      className="text-slate-400 hover:text-slate-900 border border-slate-200 p-1.5"><PencilSimple size={14} /></button>
                    <button data-testid={`fact-reject-${f.id}`} onClick={() => setRejecting(f)} title="Reject"
                      className="text-red-600 border border-red-200 p-1.5 hover:bg-red-50"><XCircle size={14} weight="bold" /></button>
                    <button data-testid={`fact-approve-${f.id}`} onClick={() => approve(f)}
                      className="bg-emerald-700 text-white text-[11px] font-semibold px-3 py-1.5 hover:bg-emerald-800 flex items-center gap-1.5">
                      <CheckCircle size={13} weight="bold" /> Approve
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </section>

      <FactAuditReport productId={productId} />

      {editing && <FactEditDialog fact={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); loadFacts(); }} />}
      {rejecting && <RejectDialog fact={rejecting} onClose={() => setRejecting(null)} onDone={() => { setRejecting(null); loadFacts(); }} />}
      <AddLinksDialog open={linksOpen} onClose={() => setLinksOpen(false)} productId={productId} onDone={() => { loadDocs(); loadFacts(); }} />
    </PageShell>
  );
}
